// src/components/ListingCard.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

// Cache for image URLs
const imageUrlCache = new Map();
// Cache for failed URLs to avoid retrying them immediately
const failedUrlCache = new Set();

// Function to validate URL structure
const validateImageUrl = (url) => {
  if (!url) return { valid: false, reason: 'URL is empty or null' };
  
  try {
    const urlObj = new URL(url);
    
    // Check for common issues
    const issues = [];
    
    if (url.includes('%C3%') || url.includes('%83%')) {
      issues.push('Double URL encoding detected');
    }
    
    if (url.includes('---')) {
      issues.push('Triple dashes in filename');
    }
    
    if (url.includes(' ')) {
      issues.push('Spaces in URL (should be encoded)');
    }
    
    if (!url.match(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i)) {
      issues.push('Missing or unrecognized file extension');
    }
    
    if (url.length > 2000) {
      issues.push('URL is very long (>2000 chars)');
    }
    
    return {
      valid: issues.length === 0,
      issues: issues,
      protocol: urlObj.protocol,
      hostname: urlObj.hostname
    };
  } catch (error) {
    return {
      valid: false,
      reason: `Invalid URL format: ${error.message}`
    };
  }
};

// Function to sanitize Google Cloud Storage URLs
const sanitizeGoogleCloudUrl = (url) => {
  if (!url || !url.includes('storage.googleapis.com')) {
    return url;
  }

  try {
    // Parse the URL to work with it
    const urlObj = new URL(url);
    let pathname = urlObj.pathname;
    
    console.log(`🔧 Original pathname: ${pathname}`);
    
    // Remove double encoding issues
    pathname = decodeURIComponent(pathname);
    console.log(`🔧 After decoding: ${pathname}`);
    
    // Fix common issues in the filename
    pathname = pathname
      .replace(/---/g, '-') // Replace triple dashes with single dash
      .replace(/\s+/g, '-') // Replace spaces with dashes
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[ýÿ]/g, 'y')
      .replace(/[ç]/g, 'c')
      .replace(/[ñ]/g, 'n');
    
    console.log(`🔧 After character replacement: ${pathname}`);
    
    // Add .jpg extension if missing
    const hasExtension = /\.(jpg|jpeg|png|gif|webp)$/i.test(pathname);
    if (!hasExtension) {
      pathname += '.jpg'; // Default to .jpg for car images
      console.log(`🔧 Added .jpg extension: ${pathname}`);
    }
    
    // Rebuild the URL
    urlObj.pathname = encodeURI(pathname);
    const sanitizedUrl = urlObj.toString();
    
    console.log(`🔧 Final sanitized URL: ${sanitizedUrl}`);
    return sanitizedUrl;
    
  } catch (error) {
    console.warn(`Failed to sanitize URL: ${url}`, error);
    return url;
  }
};

// Enhanced image URL generator - simplified for triple dash consistency
const generateImageUrl = (listing) => {
  const listingId = listing._id;
  const firstImage = listing.images && listing.images.length > 0 ? listing.images[0] : null;
  
  console.log(`🖼️ [${listingId}] Starting image URL generation`);

  if (!firstImage) {
    console.log(`🖼️ [${listingId}] No image data found, using placeholder`);
    return { 
      url: 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22600%22%20height%3D%22400%22%20viewBox%3D%220%200%20600%20400%22%20preserveAspectRatio%3D%22none%22%3E%3Cfilter%20id%3D%22b%22%3E%3CfeGaussianBlur%20stdDeviation%3D%2240%22%2F%3E%3C%2Ffilter%3E%3Crect%20width%3D%22600%22%20height%3D%22400%22%20fill%3D%22%23777%22%2F%3E%3Cg%20filter%3D%22url%28%23b%29%22%3E%3Cg%20fill-opacity%3D%22.5%22%3E%3Ccircle%20fill%3D%22%23999%22%20cx%3D%22300%22%20cy%3D%22200%22%20r%3D%22100%22%2F%3E%3Ccircle%20fill%3D%22%23aaa%22%20cx%3D%22100%22%20cy%3D%22100%22%20r%3D%2250%22%2F%3E%3Ccircle%20fill%3D%22%23888%22%20cx%3D%22500%22%20cy%3D%22300%22%20r%3D%2270%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E', 
      source: 'placeholder' 
    };
  }

  let primaryUrl = null;
  let source = '';

  // Priority 1: fileUrl (should be most reliable with triple dashes)
  if (firstImage.fileUrl && firstImage.fileUrl.includes('storage.googleapis.com')) {
    primaryUrl = firstImage.fileUrl;
    source = 'fileUrl';
  }
  // Priority 2: gcsBlobName
  else if (firstImage.gcsBlobName) {
    primaryUrl = `https://storage.googleapis.com/eurocentrumpd-bucket/${firstImage.gcsBlobName}`;
    source = 'gcsBlobName';
  }
  // Priority 3: filename
  else if (firstImage.filename) {
    primaryUrl = `https://storage.googleapis.com/eurocentrumpd-bucket/${firstImage.filename}`;
    source = 'filename';
  }

  console.log(`🖼️ [${listingId}] Primary URL: ${primaryUrl} (source: ${source})`);

  return { 
    url: primaryUrl || 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22600%22%20height%3D%22400%22%20viewBox%3D%220%200%20600%20400%22%20preserveAspectRatio%3D%22none%22%3E%3Cfilter%20id%3D%22b%22%3E%3CfeGaussianBlur%20stdDeviation%3D%2240%22%2F%3E%3C%2Ffilter%3E%3Crect%20width%3D%22600%22%20height%3D%22400%22%20fill%3D%22%23777%22%2F%3E%3Cg%20filter%3D%22url%28%23b%29%22%3E%3Cg%20fill-opacity%3D%22.5%22%3E%3Ccircle%20fill%3D%22%23999%22%20cx%3D%22300%22%20cy%3D%22200%22%20r%3D%22100%22%2F%3E%3Ccircle%20fill%3D%22%23aaa%22%20cx%3D%22100%22%20cy%3D%22100%22%20r%3D%2250%22%2F%3E%3Ccircle%20fill%3D%22%23888%22%20cx%3D%22500%22%20cy%3D%22300%22%20r%3D%2270%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E',
    source: source
  };
};

// Add cardVariants definition
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

// Styles object - needs to be inside the component function for mobile access
function ListingCard({ listing, isAdmin, onRefresh, onTagClick, onEditClick, customImageStyle, customCardStyle }) {
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [imageAttempts, setImageAttempts] = useState(0);

  const navigate = useNavigate();

  // Check if this is a neplatici listing
  const isNeplatici = listing.tags && listing.tags.includes('neplatiči');

  // Styles object - moved inside component
  const styles = {
    card: {
      border: '1px solid #ddd',
      borderRadius: '8px',
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backgroundColor: '#fff',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      position: 'relative',
      width: '350px',
      height: 'auto',
      display: 'flex',
      flexDirection: 'column',
    },
    neplaticiCard: {
      border: '2px solid #d83a3a',
      backgroundColor: '#fff5f5',
    },
    editIconContainer: {
      position: 'absolute',
      top: '10px',
      right: '10px',
      zIndex: 10,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      borderRadius: '50%',
      width: '30px',
      height: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    },
    editIcon: {
      fontSize: '14px',
    },
    imageContainer: {
      position: 'relative',
      width: '100%',
      height: '250px',
      overflow: 'hidden',
    },
    imagePlaceholder: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f0f0f0',
      zIndex: 1,
    },
    loadingSpinner: {
      width: '40px',
      height: '40px',
      border: '4px solid #f3f3f3',
      borderTop: '4px solid #3498db',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      position: 'relative',
      zIndex: 2,
    },
    editOverlay: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      color: 'white',
      padding: '10px',
      borderRadius: '50%',
      opacity: 0,
      transition: 'opacity 0.3s ease',
      fontSize: '20px',
      pointerEvents: 'none',
    },
    infoContainer: {
      padding: '1rem',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    name: {
      fontSize: '1.2rem',
      fontWeight: 'bold',
      margin: '0 0 0.5rem 0',
      color: '#333',
      lineHeight: '1.3',
    },
    location: {
      color: '#666',
      margin: '0 0 0.5rem 0',
      fontSize: '0.9rem',
    },
    price: {
      fontSize: '1.1rem',
      fontWeight: 'bold',
      color: '#2c69a0',
      margin: '0 0 1rem 0',
    },
    description: {
      color: '#555',
      fontSize: '0.9rem',
      lineHeight: '1.4',
      margin: '0 0 1rem 0',
    },
    statsRow: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '1rem',
      alignItems: 'center',
    },
    statItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.3rem',
      fontSize: '0.85rem',
      color: '#666',
    },
    icon: {
      display: 'flex',
      alignItems: 'center',
      color: '#555',
    },
    tagsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.3rem',
      marginTop: 'auto',
      height: '170px', // Increased height for better spacing and to prevent last row cutoff
      overflow: 'visible', // Changed from 'auto' to 'visible' to prevent scrolling
      alignContent: 'flex-start', // Align tags to the top of the container
    },
    tagButton: {
      backgroundColor: '#f0f0f0',
      color: '#333',
      border: 'none',
      padding: '0.3rem 0.6rem',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    },
    aktualneTagButton: {
      backgroundColor: '#28a745', // Green color for aktualné
      color: 'white',
      border: 'none',
      padding: '0.3rem 0.6rem',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    },
    predaneTagButton: {
      backgroundColor: '#dc3545', // Red color for predané
      color: 'white',
      border: 'none',
      padding: '0.3rem 0.6rem',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    },
    prenajateTagButton: {
      backgroundColor: '#ff8c00', // Orange color for prenajaté
      color: 'white',
      border: 'none',
      padding: '0.3rem 0.6rem',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    },
    customTagButton: {
      backgroundColor: '#6c757d', // Gray color to match regular tags
      color: 'white',
      border: 'none',
      padding: '0.3rem 0.6rem',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    },
  };

  // Memoize the image URL to prevent unnecessary recalculations
  const imageData = useMemo(() => {
    const cacheKey = `${listing._id}-${listing.images?.[0]?.fileUrl || listing.images?.[0]?.relativePath}`;
    
    if (imageUrlCache.has(cacheKey)) {
      const cached = imageUrlCache.get(cacheKey);
      console.log(`🖼️ [${listing._id}] Using cached image data:`, cached);
      return cached;
    }

    const generated = generateImageUrl(listing);
    imageUrlCache.set(cacheKey, generated);
    console.log(`🖼️ [${listing._id}] Generated and cached image data:`, generated);
    return generated;
  }, [listing]);

  // Set initial image URL
  useEffect(() => {
    setCurrentImageUrl(imageData.url);
    setImageAttempts(0);
    console.log(`🖼️ [${listing._id}] Set initial image URL: ${imageData.url}`);
  }, [imageData, listing._id]);

  // Reset image states when listing changes
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
    console.log(`🖼️ [${listing._id}] Reset image states for new listing`);
  }, [listing._id]);

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  const handleCardClick = (e) => {
    // Don't navigate if clicking the edit icon
    if (e.target.closest('[data-edit-icon]')) {
      return;
    }
    
    // Save scroll position for the current page
    const currentPath = window.location.pathname;
    const scrollY = window.scrollY || window.pageYOffset;
    sessionStorage.setItem(`scrollPosition-${currentPath}`, scrollY.toString());
    
    // Set flag to indicate we're leaving for a listing detail
    sessionStorage.setItem('lastListingPage', currentPath);
    
    console.log(`💾 Saved scroll position for ${currentPath}: ${scrollY}`);
    
    // Navigate to the listing detail
    navigate(`/listings/${listing._id}`);
  };

  const handleEditClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onEditClick) {
      onEditClick(listing);
    }
  };

  const handleTagClick = (e, tag) => {
    e.stopPropagation();
    if (onTagClick) {
      onTagClick(tag);
    }
  };

  const handleCustomTagClick = (e, tag) => {
    e.stopPropagation();
    // Custom tags are typically not clickable for filtering
    // but we could implement this in the future
  };

  const handleImageError = (e) => {
    const failedUrl = e.target.src;
    const listingId = listing._id;
    
    console.log(`❌ [${listingId}] Image failed to load: ${failedUrl}`);
    
    // Simply show error state - no more complex fallback cycling
    setImageError(true);
    setImageLoaded(false);
  };

  const handleImageLoad = () => {
    const listingId = listing._id;
    setImageLoaded(true);
    setImageError(false);
    
    console.log(`✅ [${listingId}] Image loaded successfully`);
    console.log(`   - URL: ${currentImageUrl}`);
    console.log(`   - Source: ${imageData.source}`);
    console.log(`   - Attempts: ${imageAttempts + 1}`);
    
    // Remove from failed cache if it was there
    if (currentImageUrl) {
      failedUrlCache.delete(currentImageUrl);
    }
  };

  // Default pocetIzieb / rozloha to 0 if missing
  const pocetIzieb = listing.pocetIzieb ?? 0;
  const rozloha = listing.rozloha ?? 0;

  // Determine whether to show each icon
  const showRooms = pocetIzieb > 0;
  const showArea = rozloha > 0;

  // SVG for room icon (pocet izieb)
  const RoomIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="3" y1="9" x2="21" y2="9"></line>
      <line x1="9" y1="21" x2="9" y2="9"></line>
    </svg>
  );

  // SVG for area icon (rozloha)
  const AreaIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3h18v18H3z"></path>
      <path d="M21 3h-6v6h6V3z"></path>
      <path d="M3 15h6v6H3v-6z"></path>
    </svg>
  );

  // Deduplicate tags using a Set with normalization
  const uniqueTags = useMemo(() => {
    if (!listing.tags || listing.tags.length === 0) return [];
    
    // Create a normalized version of each tag to detect duplicates
    const normalizedTagsMap = new Map();
    
    listing.tags.forEach(tag => {
      let normalizedTag = tag.toLowerCase();
      
      // Normalize different variations of 'prenajom'
      if (normalizedTag === 'prenajom' || normalizedTag === 'prenájom') {
        normalizedTag = 'prenájom'; // Use the accented version as canonical
      }
      
      // Normalize different variations of 'aktualne'
      if (normalizedTag === 'aktualne' || normalizedTag === 'aktuálne') {
        normalizedTag = 'aktualne'; // Use the non-accented version as canonical
      }
      
      // Keep only the first occurrence of each normalized tag
      if (!normalizedTagsMap.has(normalizedTag)) {
        normalizedTagsMap.set(normalizedTag, tag);
      }
    });
    
    // Get the unique tags
    const uniqueTagsList = Array.from(normalizedTagsMap.values());
    
    // Sort tags in the specified order:
    // 1. 'aktuálne' first
    // 2. sale type tags (na predaj, na nájom, predané, prenajaté)
    // 3. all other tags
    return uniqueTagsList.sort((a, b) => {
      const aLower = a.toLowerCase();
      const bLower = b.toLowerCase();
      
      // Define priority groups
      const isAAktualne = aLower === 'aktuálne' || aLower === 'aktualne';
      const isBAktualne = bLower === 'aktuálne' || bLower === 'aktualne';
      
      // Sale type tags
      const isASaleType = ['predaj', 'prenájom', 'prenajom', 'predané', 'prenajaté'].includes(aLower);
      const isBSaleType = ['predaj', 'prenájom', 'prenajom', 'predané', 'prenajaté'].includes(bLower);
      
      // First priority: aktualne
      if (isAAktualne && !isBAktualne) return -1;
      if (!isAAktualne && isBAktualne) return 1;
      
      // Second priority: sale type tags
      if (isASaleType && !isBSaleType) return -1;
      if (!isASaleType && isBSaleType) return 1;
      
      // If both are sale types, maintain their relative order
      if (isASaleType && isBSaleType) return 0;
      
      // For all other tags, use alphabetical order
      return aLower.localeCompare(bLower);
    });
  }, [listing.tags]);

  return (
    <motion.div
      style={{
        ...styles.card,
        ...(isNeplatici ? styles.neplaticiCard : {}),
        ...(customCardStyle || {})
      }}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.3 }}
      onClick={handleCardClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Show crayon icon if hovered AND user is admin */}
      {isHovered && isAdmin && (
        <div
          data-edit-icon
          style={styles.editIconContainer}
          onClick={handleEditClick}
          title="Edit Listing"
        >
          <span style={styles.editIcon}>✏️</span>
        </div>
      )}

      {/* TOP: Image */}
      <div style={{...styles.imageContainer, ...(customImageStyle || {})}}>
        {!currentImageUrl || currentImageUrl.includes('data:image/svg+xml') ? (
          <div style={{
            ...styles.imagePlaceholder,
            backgroundColor: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#666',
            fontSize: '14px'
          }}>
            📷 No Image
          </div>
        ) : (
          <>
            {!imageLoaded && !imageError && (
              <div style={styles.imagePlaceholder}>
                <div style={styles.loadingSpinner}></div>
              </div>
            )}
            {imageError && (
              <div style={{
                ...styles.imagePlaceholder,
                backgroundColor: '#f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#666',
                fontSize: '14px'
              }}>
                📷 Failed to load
              </div>
            )}
            <img 
              src={currentImageUrl} 
              alt={listing.name} 
              style={{
                ...styles.image,
                opacity: imageLoaded && !imageError ? 1 : 0,
                transition: 'opacity 0.3s ease-in-out'
              }} 
              onError={handleImageError}
              onLoad={handleImageLoad}
              loading="lazy"
            />
          </>
        )}
        {isAdmin && (
          <div style={styles.editOverlay}>
            <span style={styles.editIcon}>✎</span>
          </div>
        )}
      </div>

      {/* BOTTOM: Info */}
      <div style={styles.infoContainer}>
        <h3 style={styles.name}>{listing.name}</h3>
        {listing.location && (
          <p style={styles.location}>{listing.location}</p>
        )}
        <p style={styles.price}>{listing.price}</p>

        {/* Description preview */}
        {listing.tags && listing.tags.includes('neplatiči') && (
          <p style={styles.description}>{listing.description}</p>
        )}

        {/* Only show the stats row if at least one field is > 0 */}
        {(showRooms || showArea) && (
          <div style={styles.statsRow}>
            {/* Room icon => pocet izieb */}
            {showRooms && (
              <div style={styles.statItem}>
                <span style={styles.icon}><RoomIcon /></span>
                <span>{pocetIzieb}</span>
              </div>
            )}
            {/* Area icon => rozloha */}
            {showArea && (
              <div style={styles.statItem}>
                <span style={styles.icon}><AreaIcon /></span>
                <span>{rozloha} m²</span>
              </div>
            )}
          </div>
        )}
        
        {/* Tags section */}
        {uniqueTags.length > 0 && (
          <div style={styles.tagsContainer}>
            {uniqueTags.map(tag => (
              <button
                key={tag}
                style={
                  (tag === 'aktuálne' || tag === 'aktualne') ? styles.aktualneTagButton :
                  (tag === 'predané' || tag === 'predane') ? styles.predaneTagButton :
                  tag === 'prenajaté' ? styles.prenajateTagButton :
                  styles.tagButton
                }
                onClick={(e) => handleTagClick(e, tag)}
              >
                {tag === 'prenajom' || tag === 'prenájom' ? 'na nájom' : 
                 tag === 'predaj' ? 'na predaj' :
                 tag === 'prenajaté' ? 'prenajaté' :
                 (tag === 'predané' || tag === 'predane') ? 'PREDANÉ' :
                 (tag === 'aktuálne' || tag === 'aktualne') ? 'AKTUÁLNE' : tag}
              </button>
            ))}
            
            {/* Display custom tags with blue background */}
            {listing.customTags && listing.customTags.length > 0 && 
              listing.customTags.map(tag => (
                <button
                  key={`custom-${tag}`}
                  style={styles.customTagButton}
                  onClick={(e) => handleCustomTagClick(e, tag)}
                >
                  {tag}
                </button>
              ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default ListingCard;